import {
  ACCESSORIES_PROPERTIES,
  BIOMETRIC_DEVICE_TYPES,
  CHANNEL_EVENT_TYPES,
  DEVICE_TYPES,
  EVENT_STATUS,
  NOTIFICATION_MESSAGE_TYPE,
  USER_MEASUREMENTS,
} from '@/constants/device';
import {
  celsiusToFahrenheit,
  fahrenheitToCelsius,
  getDayLabelOrDate,
  getEnvNumberOfDays,
  kilogramsToPounds,
  poundsToKilograms,
} from '@/utilities/helpers/commonHelper';
import { translator } from '@vantiva-sa/homesight_humaui/Store/configStore';
import { VideoCallStatusUpdate } from '@vantiva-sa/homesight_humaui/Utilities/Constants';
import { getDayLabel } from '@vantiva-sa/homesight_humaui/Utilities/DateTime';
import { Card, Skeleton } from 'antd';
import { get, isEmpty, isNil, omit, uniq } from 'lodash';
import moment from 'moment-timezone';
import PropTypes from 'prop-types';
import { memo } from 'react';
import { useSelector } from 'react-redux';
import {
  CallCompletedIcon,
  CallDeclinedIcon,
  CallFailedIcon,
  CallIcon,
  CallMissedIcon,
  CallScheduledIcon,
} from '../../icons';
import { CATEGORIES } from '../../utils';

// Look-back window of the Recent Activity card history modals (env: RECENT_ACTIVITY_HISTORY_NUMBER_OF_DAYS).
export const RECENT_ACTIVITY_HISTORY_DAYS = getEnvNumberOfDays(
  'RECENT_ACTIVITY_HISTORY_NUMBER_OF_DAYS',
  7
);

// Whole UTC days, from the start of the first day to the end of today (used by the biometric and
// engagement modals). The stream hooks round and stabilise these, so they can be built on every render.
export const getRecentActivityDayWindow = () => ({
  startDate: moment.utc().subtract(RECENT_ACTIVITY_HISTORY_DAYS, 'days').startOf('day'),
  endDate: moment.utc().endOf('day'),
});

// UUIDs of the accessories of the given device types; one stream request covers all of them.
export const getAccessoryUuids = (accessories, deviceTypes) => {
  const allowed = new Set(deviceTypes);
  return uniq(
    (accessories || [])
      .filter((accessory) => allowed.has(get(accessory, ACCESSORIES_PROPERTIES.DEVICE_TYPE, '')))
      .map((accessory) => get(accessory, ACCESSORIES_PROPERTIES.DEVICE_UUID, ''))
      .filter(Boolean)
  );
};

export const RECENT_ACTIVITY_BIOMETRIC_CARD_TYPES = { ...BIOMETRIC_DEVICE_TYPES };

export const RECENT_ACTIVITY_SMART_HOME_CARD_TYPES = { ...omit(DEVICE_TYPES, ['COOKTOP_SENSOR']) };

export const RECENT_ACTIVITY_CARD_TYPES = {
  RECENT_CALLS: 'recent_calls',
  MOTION: 'motion',
  ENGAGEMENT: 'engagement',
  ...RECENT_ACTIVITY_BIOMETRIC_CARD_TYPES,
  ...RECENT_ACTIVITY_SMART_HOME_CARD_TYPES,
};

export const CATEGORY_CARD_MAPPING = {
  [CATEGORIES.SMART_HOME]: [...Object.values(RECENT_ACTIVITY_SMART_HOME_CARD_TYPES)],
  [CATEGORIES.MOTION]: [RECENT_ACTIVITY_CARD_TYPES.MOTION],
  [CATEGORIES.BIOMETRIC]: [...Object.values(RECENT_ACTIVITY_BIOMETRIC_CARD_TYPES)],
};

export const LoadingCard = () => (
  <Card className="recent-activity-card" variant="borderless">
    <Skeleton active round paragraph={{ rows: 3, width: '50%' }} />
  </Card>
);

// Placeholder cells shaped like a table row: date + time, then one block per remaining column.
const SkeletonRowCells = ({ columns }) => (
  <>
    <div className="row-cell date-cell">
      <Skeleton.Input active size="small" className="skeleton-date" />
      <Skeleton.Input active size="small" className="skeleton-time" />
    </div>
    {Array.from({ length: Math.max(columns - 1, 0) }, (_, index) => (
      <div className="row-cell bp-cell" key={index}>
        <Skeleton.Input
          active
          size="small"
          className={index === 0 ? 'skeleton-text' : 'skeleton-value'}
        />
      </div>
    ))}
  </>
);

SkeletonRowCells.propTypes = {
  columns: PropTypes.number.isRequired,
};

// Loading state of the Recent Activity history modals: the same table (card, header, 100px rows)
// the data will be shown in, filled with placeholders, so nothing moves or resizes when it loads.
export const LoadingModalContent = ({ columns = 3, rows = 4 }) => {
  const trans = useSelector(translator);
  return (
    <div
      className="recent-blood-pressure-table-card recent-activity-skeleton recent-activity-loading-table"
      role="status"
      aria-busy="true"
      aria-live="polite"
    >
      <div className="recent-blood-pressure-table-head">
        {Array.from({ length: columns }, (_, index) => (
          <div className={`head-cell${index === 0 ? ' head-cell-date' : ''}`} key={index}>
            <Skeleton.Input active size="small" className="skeleton-head" />
          </div>
        ))}
      </div>
      <div className="recent-blood-pressure-table-body">
        {Array.from({ length: rows }, (_, index) => (
          <div className="recent-blood-pressure-table-row" key={index}>
            <SkeletonRowCells columns={columns} />
          </div>
        ))}
      </div>
      <span className="recent-activity-sr-only">{trans('LOADING')}</span>
    </div>
  );
};

LoadingModalContent.propTypes = {
  columns: PropTypes.number,
  rows: PropTypes.number,
};

export const ErrorModalContent = () => {
  const trans = useSelector(translator);
  return <div className="recent-calls-empty">{trans('ERROR_MESSAGE')}</div>;
};

export const EmptyModalContent = () => {
  const trans = useSelector(translator);
  return <div className="recent-calls-empty">{trans('NODATAAVAIALBLE')}</div>;
};

// Optional footer for streamed modal tables: a placeholder row while more rows arrive, or an error
// note when the stream failed after some rows were shown. Pass the flags from a v2 stream hook.
export const StreamStatusFooter = ({ isStreaming = false, isPartial = false, columns = 3 }) => {
  const trans = useSelector(translator);
  if (isStreaming) {
    return (
      <div
        className="recent-activity-stream-status recent-activity-stream-skeleton recent-activity-skeleton"
        role="status"
        aria-live="polite"
      >
        {/* Same column layout as a table row, so it reads as "more rows are coming". */}
        <SkeletonRowCells columns={columns} />
        <span className="recent-activity-sr-only">{trans('LOADING')}</span>
      </div>
    );
  }
  if (isPartial) {
    return (
      <div className="recent-activity-stream-status error" role="alert">
        {trans('ERROR_MESSAGE')}
      </div>
    );
  }
  return null;
};

StreamStatusFooter.propTypes = {
  isStreaming: PropTypes.bool,
  isPartial: PropTypes.bool,
  columns: PropTypes.number,
};

export const getDateLabel = (isoTimestamp, trans, timezone, locale = 'en-US') => {
  if (!isoTimestamp) return '';

  const tzOptions = timezone ? { timeZone: timezone } : {};
  const date = new Date(isoTimestamp);

  // Get today and yesterday in the user's timezone using moment
  const today = timezone ? moment().tz(timezone).startOf('day') : moment().startOf('day');
  const eventDate = timezone
    ? moment(isoTimestamp).tz(timezone).startOf('day')
    : moment(isoTimestamp).startOf('day');
  const yesterday = today.clone().subtract(1, 'day');

  if (eventDate.isSame(today, 'day')) return trans('TODAY').trim();

  if (eventDate.isSame(yesterday, 'day')) return trans('YESTERDAY');

  return date.toLocaleDateString(locale, {
    month: '2-digit',
    day: '2-digit',
    year: '2-digit',
    ...tzOptions,
  });
};

export const formatTime = (isoTimestamp, is24Hours = false, timezone) => {
  if (!isoTimestamp) return '';
  const date = new Date(isoTimestamp);
  return date.toLocaleTimeString([], {
    hour: is24Hours ? '2-digit' : 'numeric',
    minute: '2-digit',
    hour12: !is24Hours,
    ...(timezone ? { timeZone: timezone } : {}),
  });
};

// One history row: the shared date/time cell plus the card's own cells. Memoized, so rows that are
// already shown are not re-rendered when new streamed rows are merged in. `trans` is passed down
// rather than selected per row, to avoid one Redux subscription per row.
const HistoryRow = memo(
  ({ entry, renderCells, trans, timezone, is24HourFormat, locale, measurementSystem }) => {
    const timestamp = get(entry, 'utc_timestamp_inms', '');
    return (
      <div className="recent-blood-pressure-table-row">
        <div className="row-cell date-cell">
          <span className="date-text">{getDateLabel(timestamp, trans, timezone, locale)}</span>
          <span className="time-text">{formatTime(timestamp, is24HourFormat, timezone)}</span>
        </div>
        {renderCells(entry, { trans, measurementSystem })}
      </div>
    );
  }
);

HistoryRow.displayName = 'HistoryRow';

HistoryRow.propTypes = {
  entry: PropTypes.object.isRequired,
  renderCells: PropTypes.func.isRequired,
  trans: PropTypes.func.isRequired,
  timezone: PropTypes.string,
  is24HourFormat: PropTypes.bool,
  locale: PropTypes.string,
  measurementSystem: PropTypes.string,
};

/**
 * History table of a Recent Activity modal, fed by a v2 stream hook (`useUserEventsByTypesStream` /
 * `useUserDeviceEventsStream`): spread the hook result into it. Rows render as they arrive, already
 * de-duplicated and sorted newest first by the hook.
 *
 * @param {string[]} headers Translated column titles, starting with the date column.
 * @param {(entry: Object, context: { trans, measurementSystem }) => ReactNode} renderCells The cells
 *   after the date cell. Define it at module level (stable identity) so rows stay memoized.
 */
export const HistoryModalTable = ({
  headers,
  renderCells,
  data,
  isLoading,
  isError,
  isStreaming,
  isPartial,
  getKey,
  timezone,
  is24HourFormat,
  locale,
  measurementSystem,
}) => {
  const trans = useSelector(translator);
  if (isLoading) return <LoadingModalContent columns={headers.length} />;
  if (isError) return <ErrorModalContent />;
  if (isEmpty(data)) return <EmptyModalContent />;

  return (
    <div className="recent-blood-pressure-table-card">
      <div className="recent-blood-pressure-table-head">
        {headers.map((header, index) => (
          <div className={`head-cell${index === 0 ? ' head-cell-date' : ''}`} key={index}>
            {header}
          </div>
        ))}
      </div>

      <div className="recent-blood-pressure-table-body">
        {data.map((entry, index) => (
          <HistoryRow
            key={getKey?.(entry) ?? `history-row-${index}`}
            entry={entry}
            renderCells={renderCells}
            trans={trans}
            timezone={timezone}
            is24HourFormat={is24HourFormat}
            locale={locale}
            measurementSystem={measurementSystem}
          />
        ))}
      </div>

      <StreamStatusFooter
        isStreaming={isStreaming}
        isPartial={isPartial}
        columns={headers.length}
      />
    </div>
  );
};

HistoryModalTable.propTypes = {
  headers: PropTypes.arrayOf(PropTypes.node).isRequired,
  renderCells: PropTypes.func.isRequired,
  data: PropTypes.arrayOf(PropTypes.object),
  isLoading: PropTypes.bool,
  isError: PropTypes.bool,
  isStreaming: PropTypes.bool,
  isPartial: PropTypes.bool,
  getKey: PropTypes.func,
  timezone: PropTypes.string,
  is24HourFormat: PropTypes.bool,
  locale: PropTypes.string,
  measurementSystem: PropTypes.string,
};

export const sortByLatestTimestamp = (list = [], timestampKey = 'utc_timestamp_inms') => {
  return [...list].sort((a, b) => {
    const timeA = new Date(a?.[timestampKey] || 0).getTime();
    const timeB = new Date(b?.[timestampKey] || 0).getTime();
    return timeB - timeA;
  });
};

/**
 * Determines whether a scheduled call is currently in progress by building the
 * call window from the start instant (utc_timestamp_inms / event_timestamp) and
 * the event_starttime / event_endtime time-of-day strings.
 */
export const isCallInProgress = (notification) => {
  const startInstant = notification?.utc_timestamp_inms || notification?.event_timestamp;
  if (!startInstant) return false;

  const startMoment = moment(startInstant);
  if (!startMoment.isValid()) return false;

  const toSeconds = (time) => {
    if (!time) return null;
    const [hours = 0, minutes = 0, seconds = 0] = String(time).split(':').map(Number);
    if ([hours, minutes, seconds].some((part) => Number.isNaN(part))) return null;
    return hours * 3600 + minutes * 60 + seconds;
  };

  const startSeconds = toSeconds(notification?.event_starttime);
  const endSeconds = toSeconds(notification?.event_endtime);

  let endMoment;
  if (startSeconds !== null && endSeconds !== null) {
    endMoment = startMoment.clone().add(endSeconds - startSeconds, 'seconds');
  } else if (notification?.duration_minutes) {
    endMoment = startMoment.clone().add(Number(notification.duration_minutes), 'minutes');
  } else {
    return false;
  }

  const now = moment();
  return now.isSameOrAfter(startMoment) && now.isBefore(endMoment);
};

/**
 * Merges notification call records (missed/declined/completed) with schedule events,
 * deduplicates by schedule_eventuuid, normalises timestamps, and sorts newest-first.
 */
export const mergeCallData = (notifications, scheduleEvents) => {
  const filteredNotifications = Object.values(
    (notifications || [])
      .filter(
        (e) =>
          e.message_type === NOTIFICATION_MESSAGE_TYPE.CALL_STATUS &&
          (e.call_status__status === VideoCallStatusUpdate.missed ||
            e.call_status__status === VideoCallStatusUpdate.declined ||
            e.call_status__status === VideoCallStatusUpdate.completed)
      )
      .reduce((acc, n) => {
        const key =
          n.meeting_id ||
          n.schedule_eventuuid ||
          `${n.utc_timestamp_inms ?? ''}_${n.useruuid ?? ''}`;
        const toTs = (v) => {
          const ms = Number(v);
          return isFinite(ms) ? ms : -Infinity;
        };
        if (!acc[key] || toTs(n.utc_timestamp_inms) > toTs(acc[key].utc_timestamp_inms)) {
          acc[key] = n;
        }
        return acc;
      }, {})
  );

  // Track schedule_eventuuids already represented by a notification (completed calls)
  const notifScheduleUuids = new Set(
    filteredNotifications.map((n) => n.schedule_eventuuid).filter(Boolean)
  );

  // Normalise schedule events: only include resolved events (completed/expired/ongoing),
  // skip duplicates already covered by a notification
  const normalizedScheduleEvents = (scheduleEvents || [])
    .filter((e) => Object.values(EVENT_STATUS).includes(e.event_status))
    .filter((e) => e.schedule_eventuuid && !notifScheduleUuids.has(e.schedule_eventuuid))
    .map((e) => ({
      ...e,
      utc_timestamp_inms: e.utc_timestamp_inms || e.event_timestamp,
    }));

  return sortByLatestTimestamp([...filteredNotifications, ...normalizedScheduleEvents]);
};

export const getContactName = (notification) => {
  const invitedContacts = notification?.invited_contacts;
  const userUuid = notification?.useruuid;

  if (!invitedContacts || !Array.isArray(invitedContacts) || invitedContacts.length === 0) {
    return null;
  }

  const contact =
    invitedContacts.find((c) => c?.useruuid && c.useruuid !== userUuid) || invitedContacts[0];
  const firstName = contact?.firstname || '';
  const lastName = contact?.lastname || '';
  const fullName = `${firstName} ${lastName}`.trim();
  return fullName || null;
};

export const getCallTitle = (notification, trans, useruuid) => {
  if (notification?.event_name) {
    return notification.event_name;
  }

  const invitedContacts = notification?.invited_contacts;
  const userUuid = useruuid || notification?.useruuid;
  const otherContact =
    Array.isArray(invitedContacts) && invitedContacts.length > 0
      ? invitedContacts.find((c) => c?.useruuid && c.useruuid !== userUuid) || invitedContacts[0]
      : null;
  const firstName = otherContact?.firstname || '';
  const lastName = otherContact?.lastname || '';
  const fullName = `${firstName} ${lastName}`.trim() || null;

  return fullName || trans('VIDEOCALL');
};

export const getStatusInfo = (status, trans) => {
  // Schedule events have no call_status — show as Scheduled
  if (!status) {
    return {
      text: trans('SCHEDULED'),
      className: 'info',
      icon: <CallScheduledIcon className="recent-calls-status-icon" />,
    };
  }

  if (status === VideoCallStatusUpdate.completed) {
    return {
      text: trans('COMPLETED'),
      className: 'success',
      icon: <CallCompletedIcon className="recent-calls-status-icon" />,
    };
  }

  if (status === VideoCallStatusUpdate.declined) {
    return {
      text: trans('DECLINED'),
      className: 'declined',
      icon: <CallDeclinedIcon className="recent-calls-status-icon" />,
    };
  }

  if (status === VideoCallStatusUpdate.missed) {
    return {
      text: trans('MISSED'),
      className: 'danger',
      icon: <CallMissedIcon className="recent-calls-status-icon" />,
    };
  }

  if (status === EVENT_STATUS.ONGOING) {
    return {
      text: trans('INPROGRESS'),
      className: 'ongoing',
      icon: <CallIcon className="recent-calls-status-icon" fill="#113e57" />,
    };
  }

  if (
    status === VideoCallStatusUpdate.calling ||
    status === VideoCallStatusUpdate.invited ||
    status === VideoCallStatusUpdate.ringing ||
    status === VideoCallStatusUpdate.accepted ||
    status === VideoCallStatusUpdate.joining
  ) {
    return {
      text: trans('SCHEDULED'),
      className: 'info',
      icon: <CallScheduledIcon className="recent-calls-status-icon" />,
    };
  }

  return {
    text: trans('FAILED'),
    className: 'danger',
    icon: <CallFailedIcon className="recent-calls-status-icon" />,
  };
};

export const getChannelType = (messageType) => {
  switch (messageType.toLowerCase()) {
    case CHANNEL_EVENT_TYPES.CHANNEL_LAUNCHED:
      return 'Launched';
    case CHANNEL_EVENT_TYPES.VIDEO_PLAY_STARTED:
      return 'Started';
    case CHANNEL_EVENT_TYPES.VIDEO_PLAY_COMPLETED:
      return 'Completed';
    case CHANNEL_EVENT_TYPES.VIDEO_PLAY_ABANDONED:
      return 'Abandoned';
    case CHANNEL_EVENT_TYPES.VIDEO_PLAY_ERROR:
      return 'Error';
    default:
      return '-';
  }
};

export const reduceAccessoryToMotionDevice = (accessory) => {
  if (isEmpty(accessory)) return {};

  const modelnumber = get(accessory, ACCESSORIES_PROPERTIES.MODEL_NUMBER, '');
  const serialnumber = get(accessory, ACCESSORIES_PROPERTIES.DEVICE_SERIAL_NUMBER, '');
  const location = get(accessory, ACCESSORIES_PROPERTIES.LOCATION, '');
  const battery_level = get(accessory, ACCESSORIES_PROPERTIES.DEVICE_BATTERY_LEVEL, '');
  const paired_status = get(accessory, ACCESSORIES_PROPERTIES.PAIRED_STATUS, '');
  const deviceconnectstatus = get(accessory, ACCESSORIES_PROPERTIES.DEVICE_CONNECT_STATUS, '');
  const device_connected = get(accessory, ACCESSORIES_PROPERTIES.DEVICE_CONNECTED, '');
  const connectionStatus =
    modelnumber === 'HC200'
      ? deviceconnectstatus
      : paired_status === 'UNPAIRED'
        ? false
        : device_connected
          ? 'CONNECTED'
          : 'DISCONNECTED';

  return { serialnumber, modelnumber, location, battery_level, connectionStatus };
};

export const formatTimestamp = (timestamp, timezone, locale, is24HourFormat, trans) => {
  if (timestamp == null || timestamp == undefined) {
    return { day: '-', time: '-', difference: '-', formattedDate: '-' };
  }
  const now = moment().tz(timezone);
  const inputTime = moment.tz(timestamp, timezone);

  const dayLabel = getDayLabel(timestamp, timezone);

  let time = '';
  let formattedDate = '';
  let day = '';

  if (dayLabel === 'Today' || dayLabel === 'Yesterday') {
    day = dayLabel;
  } else {
    formattedDate = getDayLabelOrDate(timestamp, timezone, locale);
    day = formattedDate;
  }

  if (is24HourFormat) {
    time = inputTime.format('HH:mm');
  } else {
    time = inputTime.format('h:mm A');
  }

  if (!dayLabel) {
    formattedDate = getDayLabelOrDate(timestamp, timezone, locale);
  }
  const difference = moment.duration(inputTime?.diff(now));
  let differenceText;

  if (difference.asMilliseconds() < 0) {
    const pastDifference = moment.duration(now?.diff(inputTime));
    differenceText =
      pastDifference.asMinutes() < 1
        ? trans('JUST_NOW_L')
        : pastDifference.asMinutes() < 60
          ? `${Math.round(pastDifference.asMinutes())} ${trans('MINAGO_L')} `
          : pastDifference.asHours() < 24
            ? `${Math.round(pastDifference.asHours())} ${trans('HOURSAGO_L')} `
            : `${Math.round(pastDifference.asDays())} ${trans('DAYSAGO_L')} `;
  } else {
    differenceText =
      difference.asMinutes() < 1
        ? trans('LATER')
        : difference.asMinutes() < 60
          ? `${trans('IN')} ${Math.round(difference.asMinutes())} ${trans('MIN_L')} `
          : difference.asHours() < 24
            ? `${trans('IN')} ${Math.round(difference.asHours())} ${trans('HOURS_L')} `
            : `${trans('IN')} ${Math.round(difference.asDays())} ${trans('DAYS_L')} `;
  }

  return {
    day,
    time,
    difference: differenceText,
    formattedDate,
  };
};

export const temperatureUnits = { metric: '°C', imperial: '°F' };

export const weightUnits = { metric: 'kgs', imperial: 'lbs' };

export const convertTemperature = (value, currentMeasurementSystem, targetMeasurementSystem) => {
  if (isNil(value) || value === '' || value === '-') return '-';
  if (currentMeasurementSystem === targetMeasurementSystem)
    return Math.round(Number(value).toFixed(1));

  return targetMeasurementSystem === USER_MEASUREMENTS.METRIC
    ? Math.round(fahrenheitToCelsius(Number(value).toFixed(1)))
    : Math.round(celsiusToFahrenheit(Number(value).toFixed(1)));
};

export const convertWeight = (value, currentMeasurementSystem, targetMeasurementSystem) => {
  if (isNil(value) || value === '' || value === '-') return '-';
  if (currentMeasurementSystem === targetMeasurementSystem) return Number(value).toFixed(1);

  return targetMeasurementSystem === USER_MEASUREMENTS.METRIC
    ? poundsToKilograms(Number(value)).toFixed(1)
    : kilogramsToPounds(Number(value)).toFixed(1);
};
